import React, { createContext, useContext, useMemo, useReducer, useState } from 'react';
import { produce, WritableDraft } from 'immer';
import { isEmpty } from 'lodash';
import uuid from 'uuid-random';
import {
  LaterExemplar,
  ExemplarWord,
  ImageSection,
  ImageSide,
  Section,
  Image,
  IDefaultImages,
  AWSImage,
  ImageDimensions,
  Side,
  ContentWithBottom,
  ReplacedImage,
  TextTypes,
} from '../types';
import { StyledWordContext } from './styledWordContext';
import { DrawingContext } from './drawingContext';
import { TextPresentationPageContext } from './textPresentationPageContext';
import { convertSavedLinesToLines, convertSavedNotesToNotes, getHiddenImagesFromSavedContent } from '../utils/editTool';
import { UndoRedoContext } from './undoRedoContext';
import { Type, UndoRedoAction } from '../hooks/useUndoRedo';
import { getLaterStateWord } from '../utils';

export enum ActionTypes {
  SET_EXEMPLAR = 'SET_EXEMPLAR',
  SET_HIGHLIGHTED_SECTION = 'SET_HIGHLIGHTED_SECTION',
  SET_HIGHLIGHTED_LANGUAGE = 'SET_HIGHLIGHTED_LANGUAGE',
  SET_FONT_STYLE = 'SET_FONT_STYLE',
  SET_LANGUAGE = 'SET_LANGUAGE',
  SET_HIGHLIGHTED = 'SET_HIGHLIGHTED',
  UPDATE_RELATIONSHIPS = 'UPDATE_RELATIONSHIPS',
  REPLACE_IMAGE = 'REPLACE_IMAGE',
  SET_INTRODUCTION_TO_HYBRID = 'SET_INTRODUCTION_TO_HYBRID',
}

const initialState: LaterExemplar = {} as LaterExemplar;

type payload =
  | ExemplarWord
  | LaterExemplar
  | ImageSection
  | IDefaultImages
  | Record<string, boolean>
  | string[]
  | boolean;

export interface IAction {
  type: string;
  payload: payload;
}

type Dispatch = {
  exemplarState: typeof initialState;
  dispatch: (action: IAction) => void;
  setExemplar: (exemplar: LaterExemplar, isForce?: boolean) => void;
  setHighlightedSection: (highlightedSections: Record<string, boolean>) => void;
  setHighlightedLanguage: (highlightedSections: Record<string, boolean>) => void;
  handleFontStyle: (word: ExemplarWord) => void;
  handleLanguage: (word: ExemplarWord) => void;
  handleWordHighlight: (word: ExemplarWord) => void;
  saveHighlightsUpdates: (prevState: ExemplarWord, state: ExemplarWord) => void;
  saveWordLanguageUpdates: (prevState: ExemplarWord, state: ExemplarWord) => void;
  updateRelationships: (defaultImages: AWSImage[]) => void;
  replaceImage: (
    imageUUIDtoReplace: string,
    uuid: string,
    default_uuid: string | undefined,
    defaultImageDimensions: ImageDimensions,
    selectedImageDimensions: ImageDimensions,
    sectionId: string,
    side?: ImageSide
  ) => void;
  setSavedEdits: (exemplar: LaterExemplar, page: string) => void;
  saveLaterImageUpdates: (prevState: ReplacedImage, state: ReplacedImage) => void;
  saveTextUpdates: (prevState: LaterExemplar, state: LaterExemplar) => void;
  isIntroducedToHybrid: undefined | boolean;
  setIntroductionToHybrid: () => void;
};

const getContentSection = (word: ExemplarWord, draft: WritableDraft<LaterExemplar>): Section => {
  let side = draft.content.left;
  if (word.side === 'right') {
    side = draft.content.right;
  }

  return side[word.sectionIndex] as Section;
};

const getProduce = (state: LaterExemplar, action: IAction) => {
  return produce(state, draft => {
    const word = action.payload as ExemplarWord;
    const section = getContentSection(word, draft);

    section.lines[word.lineIndex].words[word.wordIndex] = {
      ...section.lines[word.lineIndex].words[word.wordIndex],
      ...word,
    };
  });
};

const updateWord = (state: LaterExemplar, word: ExemplarWord) => {
  return produce(state, draft => {
    const section = getContentSection(word, draft);

    section.lines[word.lineIndex].words[word.wordIndex] = word;
  });
};

const setHighlightedSectionsOrLanguage = (
  state: LaterExemplar,
  highlighted: Record<string, boolean>,
  isSection: boolean
) => {
  return produce(state, draft => {
    isSection
      ? (draft.meta.highlightedSectionLabels = highlighted)
      : (draft.meta.highlightedLanguageFeatureLabels = highlighted);
  });
};

const getNumberOfLines = (side: Array<Section | Image>): number =>
  side.reduce((numberOfLines: number, section: Section | Image) => {
    const lineLength = section.type === 'image' ? 0 : section.lines.length;

    return numberOfLines + lineLength;
  }, 0);

export const getSideToPutImageOn = (content: ContentWithBottom | Side): ImageSide.left | ImageSide.right => {
  const leftSideNumberOfLines = getNumberOfLines(content.left);

  const rightSideNumberOfLines = getNumberOfLines(content.right);

  return leftSideNumberOfLines <= rightSideNumberOfLines ? ImageSide.left : ImageSide.right;
};

const getSideWithoutImages = (side: Array<Image>) => side.filter(item => item.type !== 'image');

// eslint-disable-next-line complexity
const replaceSideImage = (content: Side, action: IAction): Side => {
  const payload = action.payload as ImageSection;
  const { imageUUIDtoReplace, uuid, default_uuid, sectionId, side, defaultImageDimensions } = payload;

  const sideToChange = side === ImageSide.bottom ? getSideToPutImageOn(content as ContentWithBottom | Side) : side;

  if (!defaultImageDimensions) return content;

  if (uuid !== default_uuid && content.bottom) {
    return {
      ...content,
      [ImageSide.bottom]: getSideWithoutImages(content[ImageSide.bottom] as Array<Image>),
      [sideToChange]: getModifiedSide(
        content[sideToChange],
        sideToChange,
        uuid,
        imageUUIDtoReplace,
        default_uuid,
        defaultImageDimensions
      ),
    };
  }

  if (uuid !== default_uuid && !content.bottom) {
    return {
      ...content,
      [sideToChange]: getModifiedSide(
        content[sideToChange],
        sideToChange,
        uuid,
        imageUUIDtoReplace,
        default_uuid,
        defaultImageDimensions
      ),
    };
  }

  if (uuid === default_uuid && sectionId && content.bottom) {
    return {
      ...content,
      [sideToChange]: getSideWithoutImages(content[sideToChange] as Array<Image>),
      [ImageSide.bottom]: [
        ...content.bottom,
        {
          type: 'image',
          uuid,
          editable: true,
          id: sectionId,
        },
      ],
    };
  }

  if (uuid === default_uuid && !content.bottom) {
    return {
      ...content,
      [sideToChange]: getModifiedSide(
        content[sideToChange],
        sideToChange,
        uuid,
        imageUUIDtoReplace,
        default_uuid,
        defaultImageDimensions
      ),
    };
  }

  return content;
};

const getModifiedSide = (
  side: Array<Section | Image>,
  sideToChange: ImageSide | undefined,
  image_uuid: string,
  imageUUIDtoReplace: string,
  default_uuid: string | undefined,
  defaultImageDimensions: ImageDimensions
) => {
  const isSideWithImage = side && !!side.find(item => item.type === 'image');

  if (isSideWithImage) {
    return [
      ...(side as Image[]).map(item => {
        if (item.type === 'image') {
          if (item.uuid === imageUUIDtoReplace) {
            if (!item.default_uuid) {
              return {
                ...item,
                uuid: image_uuid,
                default_uuid: item.uuid,
                defaultImageDimensions,
              };
            }

            if (item.default_uuid === default_uuid) {
              return {
                ...item,
                uuid: image_uuid,
              };
            }
          }
          return item;
        }
        return item;
      }),
    ];
  } else {
    return [
      ...side,
      {
        type: 'image',
        editable: true,
        uuid: image_uuid,
        default_uuid: default_uuid ? default_uuid : imageUUIDtoReplace,
        id: uuid(),
        position: {
          x: sideToChange === ImageSide.left ? 0 : 53,
          y:
            ((defaultImageDimensions.exemplarHeight - defaultImageDimensions?.height) /
              defaultImageDimensions.exemplarHeight) *
            100,
          height: (defaultImageDimensions.height / defaultImageDimensions.exemplarHeight) * 100,
          width: sideToChange === ImageSide.left ? 50 : undefined,
        },
      },
    ];
  }
};

const exemplarReducer = (state: LaterExemplar, action: IAction): LaterExemplar => {
  switch (action.type) {
    case ActionTypes.SET_EXEMPLAR:
      return {
        ...(action.payload as LaterExemplar),
      };
    case ActionTypes.SET_HIGHLIGHTED_SECTION:
      return setHighlightedSectionsOrLanguage(state, action.payload as Record<string, boolean>, true);
    case ActionTypes.SET_HIGHLIGHTED_LANGUAGE:
      return setHighlightedSectionsOrLanguage(state, action.payload as Record<string, boolean>, false);
    case ActionTypes.SET_FONT_STYLE:
      return getProduce(state, action);
    case ActionTypes.SET_LANGUAGE:
      return updateWord(state, action.payload as ExemplarWord);
    case ActionTypes.SET_HIGHLIGHTED:
      return updateWord(state, action.payload as ExemplarWord);
    case ActionTypes.UPDATE_RELATIONSHIPS:
      return {
        ...state,
        relationships: {
          ...state.relationships,
          images: [...state.relationships.images, ...(action.payload as IDefaultImages).defaultImages],
        },
      };
    case ActionTypes.REPLACE_IMAGE:
      return {
        ...state,
        content: replaceSideImage(state.content, action),
      };
    case ActionTypes.SET_INTRODUCTION_TO_HYBRID:
      return {
        ...state,
        meta: {
          ...state.meta,
          isIntroducedToHybrid: action.payload as boolean,
        },
      };
    default:
      return state;
  }
};

export const ExemplarContext = createContext<Dispatch>({
  exemplarState: initialState,
  dispatch: () => false,
  setExemplar: () => false,
  setHighlightedSection: () => false,
  setHighlightedLanguage: () => false,
  handleFontStyle: () => false,
  handleLanguage: () => false,
  handleWordHighlight: () => false,
  saveHighlightsUpdates: () => null,
  saveWordLanguageUpdates: () => null,
  updateRelationships: () => false,
  replaceImage: () => false,
  setSavedEdits: () => null,
  saveLaterImageUpdates: () => null,
  saveTextUpdates: () => null,
  isIntroducedToHybrid: false,
  setIntroductionToHybrid: () => null,
});

export const ExemplarProvider: React.FC = ({ children }): JSX.Element => {
  const [state, dispatch] = useReducer(exemplarReducer, initialState);
  const { handleHideText, setImages, handleHideHighlightsTool } = useContext(StyledWordContext);
  const { lines, setLines, setDrawingsHidden } = useContext(DrawingContext);
  const { setNotes } = useContext(TextPresentationPageContext);
  const { registerUndoRedoCreator } = useContext(UndoRedoContext);
  const [isSavedExemplarSet, setSavedExemplar] = useState(false);

  const setExemplar = (exemplar: LaterExemplar, isForce = false) => {
    const { meta } = state;
    const payload = {
      ...exemplar,
      meta: {
        isIntroducedToHybrid: meta?.isIntroducedToHybrid === undefined && exemplar.text_type === TextTypes.hybrid,
        highlightedLanguageFeatureLabels: {},
        highlightedSectionLabels: {},
      },
    };

    if (isForce || isEmpty(state)) {
      dispatch({ type: ActionTypes.SET_EXEMPLAR, payload: payload });
    }
  };

  const setHighlightedSection = (highlighted: Record<string, boolean>) => {
    dispatch({ type: ActionTypes.SET_HIGHLIGHTED_SECTION, payload: highlighted });
  };

  const setHighlightedLanguage = (highlighted: Record<string, boolean>) => {
    dispatch({ type: ActionTypes.SET_HIGHLIGHTED_LANGUAGE, payload: highlighted });
  };

  const handleFontStyle = (word: ExemplarWord) => {
    const { language, phrase, prepositional_phrase, ...payloadWord } = getLaterStateWord(state, word);

    dispatch({ type: ActionTypes.SET_FONT_STYLE, payload: { ...payloadWord, style: word.style } });
  };

  const handleLanguage = (word: ExemplarWord) => {
    dispatch({ type: ActionTypes.SET_LANGUAGE, payload: word });
  };

  const handleUpdateRelationships = (defaultImages: AWSImage[]) => {
    dispatch({ type: ActionTypes.UPDATE_RELATIONSHIPS, payload: { defaultImages } });
  };

  const handleWordHighlight = (word: ExemplarWord) => {
    dispatch({ type: ActionTypes.SET_HIGHLIGHTED, payload: word });
  };

  const saveHighlightsUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.highlight, (payload: UndoRedoAction) => {
        handleWordHighlight(payload as ExemplarWord);
      }),
    []
  );

  const saveWordLanguageUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.setLanguage, (payload: UndoRedoAction) => {
        handleLanguage(payload as ExemplarWord);
      }),
    []
  );

  const replaceImage = (
    imageUUIDtoReplace: string,
    uuid: string,
    default_uuid: string | undefined,
    defaultImageDimensions: ImageDimensions,
    selectedImageDimensions: ImageDimensions,
    sectionId: string,
    side?: ImageSide
  ) => {
    if (!side) return;

    dispatch({
      type: ActionTypes.REPLACE_IMAGE,
      payload: {
        imageUUIDtoReplace,
        uuid,
        default_uuid,
        sectionId,
        side,
        defaultImageDimensions,
        selectedImageDimensions,
      },
    });
  };

  const setSavedEdits = (exemplar: LaterExemplar, page: string) => {
    const { tools } = exemplar;

    if (tools && !isSavedExemplarSet) {
      setImages(getHiddenImagesFromSavedContent(exemplar.content));
      setSavedExemplar(true);
      const { pen, notes, isTextHidden, highlights } = tools;

      handleHideText(!!isTextHidden);

      if (pen) {
        const { lines: savedLines, isHidden } = pen;
        setLines({ ...lines, [page]: convertSavedLinesToLines(savedLines) });
        setDrawingsHidden(isHidden);
      }

      if (notes) {
        setNotes(convertSavedNotesToNotes(notes));
      }

      if (highlights?.isHidden) {
        handleHideHighlightsTool();
      }
    }
  };

  const saveImageUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.replaceImage, (payload: UndoRedoAction) => {
        const {
          imageUUIDtoReplace,
          uuid,
          default_uuid,
          sectionId,
          side,
          defaultImageDimensions,
          selectedImageDimensions,
        } = payload as ReplacedImage;
        replaceImage(
          imageUUIDtoReplace,
          uuid,
          default_uuid,
          defaultImageDimensions,
          selectedImageDimensions,
          sectionId,
          side
        );
      }),
    []
  );

  const saveTextUpdates = registerUndoRedoCreator(Type.updateExemplarText, (payload: UndoRedoAction) => {
    setExemplar(payload as LaterExemplar, true);
  });

  const setIntroductionToHybrid = () => {
    dispatch({ type: ActionTypes.SET_INTRODUCTION_TO_HYBRID, payload: false });
  };

  return (
    <ExemplarContext.Provider
      value={{
        dispatch: dispatch,
        exemplarState: state,
        setExemplar,
        setHighlightedSection,
        setHighlightedLanguage,
        handleFontStyle,
        handleLanguage,
        handleWordHighlight,
        saveHighlightsUpdates,
        saveWordLanguageUpdates,
        updateRelationships: handleUpdateRelationships,
        replaceImage,
        setSavedEdits,
        saveLaterImageUpdates: saveImageUpdates,
        saveTextUpdates,
        isIntroducedToHybrid: state?.meta?.isIntroducedToHybrid,
        setIntroductionToHybrid,
      }}
    >
      {children}
    </ExemplarContext.Provider>
  );
};
